import { Grid, makeStyles, Paper, Typography } from "@material-ui/core";
import { SingleSelectField } from "@saleor/components/SingleSelectField";
import { ValidateFormProps } from "@saleor/createOrder/views/CreateOrderView";
import {
  ContractOrderQuery,
  useGetDistributionChannelQuery,
  useGetListSaleGroupsQuery,
  useGetListSaleOrganizationsQuery,
  useGetListSalesOfficesQuery,
  useGetListScgDivisionQuery
} from "@saleor/graphql";
import { mapNodeToChoiceWithCode } from "@saleor/utils/maps";
import { useFormik } from "formik";
import React, { FC, useEffect, useMemo, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import * as Yup from "yup";

const useStyles = makeStyles(
  theme => ({
    container: {
      maxWidth: "40%",
      "& .MuiGrid-item": {
        display: "flex",
        alignItems: "center"
      },
      margin: "60px 0 120px 0"
    },
    body: {
      padding: 24
    },
    header: {
      padding: "1rem 1.6rem",
      background: theme.palette.gardient.medium,
      colors: "white",
      display: "flex",
      alignItems: "center",
      borderTopRightRadius: 6,
      borderTopLeftRadius: 6
    },
    title: {
      color: "white",
      marginRight: "4px"
    },
    subtitle: {
      fontWeight: 400
    },
    fieldLabel: {
      flexFlow: "row-reverse",
      color: theme.palette.grey[900]
    },
    buttonReset: {
      width: "100%"
    },
    selectField: {
      "& .MuiSelect-root": {
        padding: "0.8rem 1.2rem",
        color: theme.palette.grey[800]
      }
    }
  }),
  { name: "UserChangeDrawer" }
);

interface Props {
  dataOrder: ContractOrderQuery["contractOrder"];
  handleOrganizationInfo: (values: any) => void;
  validateForm: ValidateFormProps;
  setValidateForm: (values: ValidateFormProps) => void;
}

const SaleOrgDetails: FC<Props> = ({
  handleOrganizationInfo,
  dataOrder,
  validateForm,
  setValidateForm
}) => {
  const [customer, setCustomer] = useState<any>({
    customer: {
      company: {},
      division: {},
      distributionChannel: {},
      office: {},
      saleGroup: {}
    }
  });
  const classes = useStyles();
  const intl = useIntl();

  const { data: dataChannel } = useGetDistributionChannelQuery({
    displayLoader: true
  });

  const { data: dataSaleOrganization } = useGetListSaleOrganizationsQuery({
    displayLoader: true
  });

  const { data: dataDivisions } = useGetListScgDivisionQuery({
    displayLoader: true
  });

  const { data: dataSaleOffices } = useGetListSalesOfficesQuery({
    displayLoader: true
  });

  const { data: dataSaleGroup } = useGetListSaleGroupsQuery({
    displayLoader: true
  });

  const divisonChoices = useMemo(
    () => mapNodeToChoiceWithCode(dataDivisions?.scgDivisions),
    [dataDivisions]
  );
  const officeChoices = useMemo(
    () => mapNodeToChoiceWithCode(dataSaleOffices?.salesOffices),
    [dataSaleOffices]
  );
  const saleOrganizationChoices = useMemo(
    () => mapNodeToChoiceWithCode(dataSaleOrganization?.salesOrganizations),
    [dataSaleOrganization]
  );
  const distributionChannelChoices = useMemo(
    () => mapNodeToChoiceWithCode(dataChannel?.distributionChannels),
    [dataChannel]
  );
  const saleGroupChoices = useMemo(
    () => mapNodeToChoiceWithCode(dataSaleGroup?.salesGroups),
    [dataSaleGroup]
  );
  // Functional

  const onChangeInfor = (property, value) => {
    const _customer: any = { ...customer };
    switch (property) {
      case "company":
        const company = saleOrganizationChoices.find(
          item => item.value === value
        );
        _customer.customer.company = {
          ..._customer.customer.company,
          id: company.value,
          name: company.label
        };
        break;
      case "distributionChannel":
        const channel = distributionChannelChoices.find(
          item => item.value === value
        );
        _customer.customer.distributionChannel = {
          ..._customer.customer.distributionChannel,
          id: channel.value,
          name: channel.label
        };
        break;
      case "division":
        const division = divisonChoices.find(item => item.value === value);
        _customer.customer.division = {
          ..._customer.customer.division,
          id: division.value,
          name: division.label
        };
        break;
      case "office":
        const office = officeChoices.find(item => item.value === value);
        _customer.customer.office = {
          ..._customer.customer.office,
          id: office.value,
          name: office.label
        };
        break;
      case "salesGroup":
        const salesGroup = saleGroupChoices.find(item => item.value === value);
        _customer.customer.saleGroup = {
          ..._customer.customer.saleGroup,
          id: salesGroup.value,
          name: salesGroup.label
        };
        break;
    }
    setCustomer(_customer);
    handleOrganizationInfo({
      saleOrganizationId: _customer?.customer?.company?.id,
      distributionChannelId: _customer?.customer?.distributionChannel?.id,
      divisionId: _customer?.customer?.division?.id,
      saleOfficeId: _customer?.customer?.office?.id,
      saleGroupId: _customer?.customer?.saleGroup?.id
    });
  };

  const CreateOrderSchema = Yup.object().shape({
    company: Yup.string().required("Required"),
    division: Yup.string().required("Required"),
    distributionChannel: Yup.string().required("Required"),
    office: Yup.string().required("Required"),
    saleGroup: Yup.string().required("Required")
  });

  const formik = useFormik({
    initialValues: {
      company: "",
      division: "",
      distributionChannel: "",
      office: "",
      saleGroup: ""
    },
    onSubmit: () => {
      // TODO: Submit form
    },
    validationSchema: CreateOrderSchema
  });

  useEffect(() => {
    if (validateForm?.isCheckValidate && !formik.isValid) {
      // Verify validate form
      formik.handleSubmit();
    }

    // Validate submit
    setValidateForm({
      ...validateForm,
      isValidate: { ...validateForm.isValidate, validateSale: formik.isValid }
    });
  }, [validateForm?.isCheckValidate, formik.isValid]);

  const convertData = data => ({
    id: data?.value,
    name: data?.label
  });
  useEffect(() => {
    // Set default value
    if (dataOrder) {
      setCustomer({
        ...customer,
        customer: {
          ...customer.customer,
          company:
            dataOrder?.saleOrganization ||
            convertData(saleOrganizationChoices?.[0]),
          division: dataOrder?.division || convertData(divisonChoices?.[0]),
          distributionChannel:
            dataOrder?.distributionChannel ||
            convertData(distributionChannelChoices?.[0]),
          office: dataOrder?.saleOffice || convertData(officeChoices?.[0]),
          saleGroup: dataOrder?.saleGroup || convertData(saleGroupChoices?.[0])
        }
      });

      formik.setValues({
        company:
          dataOrder?.saleOrganization?.id ||
          saleOrganizationChoices?.[0]?.value,
        division: dataOrder?.division?.id || divisonChoices?.[0]?.value,
        distributionChannel:
          dataOrder?.distributionChannel?.id ||
          distributionChannelChoices?.[0]?.value,
        office: dataOrder?.saleOffice?.id || officeChoices?.[0]?.value,
        saleGroup: dataOrder?.saleGroup?.id || saleGroupChoices?.[0]?.value
      });
    }
    handleOrganizationInfo({
      saleOrganizationId:
        dataOrder?.saleOrganization?.id || saleOrganizationChoices?.[0]?.value,
      distributionChannelId:
        dataOrder?.distributionChannel?.id ||
        distributionChannelChoices?.[0]?.value,
      divisionId: dataOrder?.division?.id || divisonChoices?.[0]?.value,
      saleOfficeId: dataOrder?.saleOffice?.id || officeChoices?.[0]?.value,
      saleGroupId: dataOrder?.saleGroup?.id || saleGroupChoices?.[0]?.value
    });
  }, [
    dataOrder,
    divisonChoices,
    officeChoices,
    saleOrganizationChoices,
    distributionChannelChoices,
    saleGroupChoices
  ]);

  return (
    <Paper className={classes.container}>
      <div className={classes.header}>
        <Typography variant="h6" className={classes.title}>
          {intl.formatMessage({
            defaultMessage: "ข้อมูลหน่วยงาน"
          })}
          <span className={classes.subtitle}> (ผู้สร้างคำสั่งซื้อ)</span>
        </Typography>
      </div>
      <form onSubmit={formik.handleSubmit}>
        <div className={classes.body}>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5} className={classes.fieldLabel}>
              <FormattedMessage defaultMessage={"Sales Organization"} />
            </Grid>
            <Grid item xs={12} md={7}>
              <SingleSelectField
                className={classes.selectField}
                choices={saleOrganizationChoices}
                onChange={value => {
                  onChangeInfor("company", value.target.value);
                  formik.handleChange(value);
                }}
                placeholder={"Select an option..."}
                value={formik.values.company || customer?.customer?.company?.id}
                name={"company"}
                error={!!(formik.errors.company && formik.touched.company)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5} className={classes.fieldLabel}>
              <FormattedMessage defaultMessage={"Distribution Channel"} />
            </Grid>
            <Grid item xs={12} md={7}>
              <SingleSelectField
                className={classes.selectField}
                choices={distributionChannelChoices}
                onChange={value => {
                  onChangeInfor("distributionChannel", value.target.value);
                  formik.handleChange(value);
                }}
                placeholder={"Select an option..."}
                value={
                  formik.values.distributionChannel ||
                  customer?.customer?.distributionChannel?.id
                }
                name={"distributionChannel"}
                error={
                  !!(
                    formik.errors.distributionChannel &&
                    formik.touched.distributionChannel
                  )
                }
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5} className={classes.fieldLabel}>
              <FormattedMessage defaultMessage={"Division"} />
            </Grid>
            <Grid item xs={12} md={7}>
              <SingleSelectField
                className={classes.selectField}
                choices={divisonChoices}
                onChange={value => {
                  onChangeInfor("division", value.target.value);
                  formik.handleChange(value);
                }}
                placeholder={"Select an option..."}
                value={
                  formik.values.division || customer?.customer?.division?.id
                }
                name={"division"}
                error={!!(formik.errors.division && formik.touched.division)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5} className={classes.fieldLabel}>
              <FormattedMessage defaultMessage={"Sales Office"} />
            </Grid>
            <Grid item xs={12} md={7}>
              <SingleSelectField
                className={classes.selectField}
                choices={officeChoices}
                onChange={value => {
                  onChangeInfor("office", value.target.value);
                  formik.handleChange(value);
                }}
                placeholder={"Select an option..."}
                value={formik.values.office || customer?.customer?.office?.id}
                name={"office"}
                error={!!(formik.errors.office && formik.touched.office)}
              />
            </Grid>
          </Grid>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5} className={classes.fieldLabel}>
              <FormattedMessage defaultMessage={"Sales Group"} />
            </Grid>
            <Grid item xs={12} md={7}>
              <SingleSelectField
                className={classes.selectField}
                choices={saleGroupChoices}
                onChange={value => {
                  onChangeInfor("salesGroup", value.target.value);
                  formik.handleChange(value);
                }}
                placeholder={"Select an option..."}
                value={
                  formik.values.saleGroup || customer?.customer?.salesGroup?.id
                }
                name={"saleGroup"}
                error={!!(formik.errors.saleGroup && formik.touched.saleGroup)}
              />
            </Grid>
          </Grid>
        </div>
      </form>
    </Paper>
  );
};

export default SaleOrgDetails;
